import { create } from "zustand";
import { DEFAULT_RUN_DIFFICULTY, extraDiceCost } from "@roguelife/engine";
import { api } from "./api";
import { localArtUrl } from "./art";
import type {
  ActionView,
  ChallengeView,
  EventView,
  GrantedItemView,
  Rolled,
  RunDifficultyLevel,
  RunMode,
  RunResultView,
  UserView,
} from "./types";

const RUN_KEY = "roguelife.runId";
const USER_KEY = "roguelife.user";

type LocalUser = Pick<UserView, "id" | "name">;
type ClientRoll = { id: number; numDice: number };

function randomId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `user-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readLocalUser(): LocalUser {
  const raw = localStorage.getItem(USER_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<LocalUser>;
      if (parsed.id && parsed.name) return { id: parsed.id, name: parsed.name };
    } catch {
      /* fall through and create a new local user */
    }
  }
  const id = randomId();
  const user = { id, name: `旅人${id.slice(0, 4)}` };
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

function writeLocalUser(user: LocalUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export type LogEntry =
  | { id: number; kind: "event"; name: string; des: string; imageUrl: string | null }
  | { id: number; kind: "items"; items: GrantedItemView[] }
  | { id: number; kind: "challenges"; items: ChallengeView[] }
  | { id: number; kind: "action"; name: string }
  | { id: number; kind: "result"; rolled: Rolled; req: number }
  | { id: number; kind: "system"; text: string };

interface GameState {
  runId: string | null;
  view: EventView | null;
  log: LogEntry[];
  busy: boolean;
  generating: boolean;
  historyBusy: boolean;
  error: string | null;
  extraDice: number;
  difficulty: RunDifficultyLevel;
  user: UserView | null;
  results: RunResultView[];
  started: boolean;
  selectedAction: ActionView | null;
  clientRoll: ClientRoll | null;
  rollAnimationId: number | null;
  rollRevealed: boolean;
  pendingRollLog: LogEntry[];

  initUser: () => Promise<void>;
  setUserName: (name: string) => Promise<void>;
  loadResults: () => Promise<void>;
  setDifficulty: (difficulty: RunDifficultyLevel) => void;
  start: (opts: {
    mode: RunMode;
    lang?: string;
    themePrompt?: string;
    difficulty?: RunDifficultyLevel;
  }) => Promise<void>;
  resume: () => Promise<void>;
  previewAction: (action: ActionView) => void;
  confirmAction: () => Promise<void>;
  setExtraDice: (n: number) => void;
  rollDice: () => Promise<void>;
  revealRollResult: (animationId: number) => void;
  cont: () => Promise<void>;
  reset: () => void;
}

let logSeq = 1;
const nextId = () => logSeq++;
let rollSeq = 1;
const nextRollAnimationId = () => rollSeq++;

export const useGame = create<GameState>((set, get) => {
  async function loadResultsFor(userId: string) {
    set({ historyBusy: true });
    try {
      const { results } = await api.userResults(userId);
      set({ results });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      set({ historyBusy: false });
    }
  }

  async function ensureUser(): Promise<UserView> {
    const current = get().user;
    if (current) return current;
    const local = readLocalUser();
    const { user } = await api.upsertUser(local);
    writeLocalUser({ id: user.id, name: user.name });
    set({ user });
    return user;
  }

  /** Append the narrative blocks produced by entering an event. */
  function appendEntered(view: EventView) {
    const entries: LogEntry[] = [];
    if (view.event.id !== "end" || view.status === "ended") {
      entries.push({
        id: nextId(),
        kind: "event",
        name: view.event.name,
        des: view.event.des,
        imageUrl: view.event.imageUrl ?? localArtUrl(`event:${view.event.id}`),
      });
    }
    if (view.grantedItems.length)
      entries.push({
        id: nextId(),
        kind: "items",
        items: view.grantedItems.map((it) => ({
          ...it,
          imageUrl: it.imageUrl ?? localArtUrl(`item:${it.id}`),
        })),
      });
    if (view.challenges.length)
      entries.push({ id: nextId(), kind: "challenges", items: view.challenges });
    set((s) => ({ log: [...s.log, ...entries] }));
  }

  async function guard<T>(fn: () => Promise<T>): Promise<T | undefined> {
    set({ busy: true, error: null });
    try {
      return await fn();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), clientRoll: null });
      return undefined;
    } finally {
      set({ busy: false, generating: false });
    }
  }

  function rollLogEntries(
    prevLuck: number,
    view: EventView,
    spendLabel = "重新检定",
    expectedSpend = 0,
  ): LogEntry[] {
    const newLuck = view.player.luck ?? 0;
    const entries: LogEntry[] = [];
    const spent = expectedSpend > 0 ? expectedSpend : Math.max(0, prevLuck - newLuck);
    if (spent > 0)
      entries.push({
        id: nextId(),
        kind: "system",
        text: `🎲 ${spendLabel}（-${spent} 气运）`,
      });
    if (view.pending?.rolled)
      entries.push({
        id: nextId(),
        kind: "result",
        rolled: view.pending.rolled,
        req: view.pending.requiredPoints,
      });
    // A critical success refunds luck.
    const gained = newLuck - (prevLuck - spent);
    if (gained > 0)
      entries.push({
        id: nextId(),
        kind: "system",
        text: `✦ 大成功！气运 +${gained}`,
      });
    return entries;
  }

  return {
    runId: null,
    view: null,
    log: [],
    busy: false,
    generating: false,
    historyBusy: false,
    error: null,
    extraDice: 0,
    difficulty: DEFAULT_RUN_DIFFICULTY,
    user: null,
    results: [],
    started: false,
    selectedAction: null,
    clientRoll: null,
    rollAnimationId: null,
    rollRevealed: true,
    pendingRollLog: [],

    async initUser() {
      const local = readLocalUser();
      set({
        user: {
          id: local.id,
          name: local.name,
          created_at: 0,
          updated_at: 0,
        },
      });
      try {
        const { user } = await api.upsertUser(local);
        writeLocalUser({ id: user.id, name: user.name });
        set({ user });
        await loadResultsFor(user.id);
      } catch (e) {
        set({ error: e instanceof Error ? e.message : String(e) });
      }
    },

    async setUserName(name) {
      const trimmed = name.trim() || "无名旅人";
      const current = get().user ?? {
        ...readLocalUser(),
        created_at: 0,
        updated_at: 0,
      };
      const local = { id: current.id, name: trimmed };
      writeLocalUser(local);
      set({ user: { ...current, name: trimmed } });
      try {
        const { user } = await api.upsertUser(local);
        set({ user });
      } catch (e) {
        set({ error: e instanceof Error ? e.message : String(e) });
      }
    },

    async loadResults() {
      const user = get().user ?? (await ensureUser());
      await loadResultsFor(user.id);
    },

    setDifficulty(difficulty) {
      set({ difficulty, error: null });
    },

    async start(opts) {
      await guard(async () => {
        const user = await ensureUser();
        const difficulty = opts.difficulty ?? get().difficulty;
        set({
          log: [],
          view: null,
          started: true,
          selectedAction: null,
          clientRoll: null,
          extraDice: 0,
          rollAnimationId: null,
          rollRevealed: true,
          pendingRollLog: [],
        });
        const { runId, view } = await api.newRun({
          ...opts,
          userId: user.id,
          difficulty,
        });
        localStorage.setItem(RUN_KEY, runId);
        set({ runId, view, selectedAction: null, difficulty: view.difficulty.level });
        appendEntered(view);
      });
    },

    async resume() {
      const id = localStorage.getItem(RUN_KEY);
      if (!id) return;
      // Resume is best-effort: a stale/abandoned run id should silently fall
      // back to the start screen, not surface an error toast.
      set({ busy: true, error: null });
      try {
        const { view } = await api.getRun(id);
        set({
          runId: id,
          view,
          started: true,
          log: [
            {
              id: nextId(),
              kind: "system",
              text: `旧路续上，${view.event.name} 的风声仍在眼前。`,
            },
          ],
          selectedAction: null,
          clientRoll: null,
          difficulty: view.difficulty.level,
          extraDice: 0,
          rollAnimationId: null,
          rollRevealed: true,
          pendingRollLog: [],
        });
        appendEntered(view);
      } catch {
        localStorage.removeItem(RUN_KEY);
        set({
          runId: null,
          started: false,
          selectedAction: null,
          clientRoll: null,
          extraDice: 0,
          rollAnimationId: null,
          rollRevealed: true,
          pendingRollLog: [],
        });
      } finally {
        set({ busy: false });
      }
    },

    previewAction(action) {
      if (get().view?.pending) return;
      set({
        selectedAction: action,
        clientRoll: null,
        extraDice: 0,
        error: null,
        pendingRollLog: [],
      });
    },

    async confirmAction() {
      const id = get().runId;
      const action = get().selectedAction;
      if (!id || !action) return;
      await guard(async () => {
        const prevLuck = get().view?.player.luck ?? 0;
        const numDice = 2 + get().extraDice;
        const clientRoll = action.needsCheck ? { id: nextRollAnimationId(), numDice } : null;
        set((s) => ({
          log: [...s.log, { id: nextId(), kind: "action", name: action.name }],
          clientRoll,
          rollAnimationId: null,
          rollRevealed: true,
          pendingRollLog: [],
        }));
        const selected = await api.select(id, action.id, numDice);
        if (!selected.view.pending?.needsCheck) {
          set({ view: selected.view, selectedAction: null, clientRoll: null });
          set({ generating: selected.view.mode === "infinite" });
          const { view } = await api.continue(id);
          set({ view, selectedAction: null, clientRoll: null });
          appendEntered(view);
          if (view.status === "ended") await get().loadResults();
          return;
        }

        const extraCost = extraDiceCost(get().extraDice);
        const { view } = await api.roll(id, numDice);
        const entries = rollLogEntries(prevLuck, view, "额外骰子", extraCost);
        set({
          view,
          selectedAction: null,
          clientRoll: null,
          rollAnimationId: clientRoll?.id ?? nextRollAnimationId(),
          rollRevealed: false,
          pendingRollLog: entries,
        });
      });
    },

    setExtraDice(n) {
      set({ extraDice: Math.max(0, Math.trunc(n) || 0) });
    },

    async rollDice() {
      const id = get().runId;
      if (!id) return;
      if (get().clientRoll) return;
      if (get().rollAnimationId && !get().rollRevealed) return;
      await guard(async () => {
        const prevLuck = get().view?.player.luck ?? 0;
        const numDice = get().view?.pending?.numDice ?? 2 + get().extraDice;
        const animationId = nextRollAnimationId();
        set({
          clientRoll: { id: animationId, numDice },
          rollAnimationId: null,
          rollRevealed: true,
          pendingRollLog: [],
        });
        const { view } = await api.roll(id, numDice);
        const entries = rollLogEntries(prevLuck, view);
        set({
          view,
          clientRoll: null,
          rollAnimationId: animationId,
          rollRevealed: false,
          pendingRollLog: entries,
        });
      });
    },

    revealRollResult(animationId) {
      const s = get();
      if (s.rollAnimationId !== animationId || s.rollRevealed) return;
      set({
        clientRoll: null,
        rollRevealed: true,
        pendingRollLog: [],
        log: [...s.log, ...s.pendingRollLog],
      });
    },

    async cont() {
      const id = get().runId;
      if (!id) return;
      if (get().clientRoll) return;
      if (get().rollAnimationId && !get().rollRevealed) return;
      const infinite = get().view?.mode === "infinite";
      set({ generating: infinite });
      await guard(async () => {
        const { view } = await api.continue(id);
        set({
          view,
          selectedAction: null,
          clientRoll: null,
          rollAnimationId: null,
          rollRevealed: true,
          pendingRollLog: [],
        });
        appendEntered(view);
        if (view.status === "ended") await get().loadResults();
      });
    },

    reset() {
      localStorage.removeItem(RUN_KEY);
      set({
        runId: null,
        view: null,
        log: [],
        started: false,
        error: null,
        selectedAction: null,
        clientRoll: null,
        extraDice: 0,
        rollAnimationId: null,
        rollRevealed: true,
        pendingRollLog: [],
      });
    },
  };
});
